import { Editor, Node, Path, PathRef, Transforms } from 'slate'

import { CustomEditor } from 'core-components/library/richTextEditor'

import { isList, isListItem } from './isListHelper'
import { getChildSubListItemRef, moveChildSubListUnderItem } from './moveChildSubList'

// A nested list is always a direct child of a list item of the parent list,
// so only items of such lists have a level to be moved up to
const isNestedListItem = (editor: CustomEditor, listItemPath: Path) => {
  const listPath = Path.parent(listItemPath)

  // a list at the root of the editor has no path to take a parent of,
  // and the editor itself never passes for a list
  if (!isList(Node.get(editor, listPath))) {
    return false
  }

  return isListItem(Node.get(editor, Path.parent(listPath)))
}

const getSelectedNestedListItemRefs = (editor: CustomEditor): PathRef[] => {
  const selection = editor.selection

  if (!selection) {
    return []
  }

  const selectedListItems = [
    ...Editor.nodes(editor, { at: selection, match: isListItem, mode: 'lowest' }),
  ]

  return selectedListItems.reduce((listItemRefs: PathRef[], [, listItemPath]) => {
    if (!isNestedListItem(editor, listItemPath)) {
      return listItemRefs
    }

    return [...listItemRefs, Editor.pathRef(editor, listItemPath)]
  }, [])
}

export const decreaseListLevel = (editor: CustomEditor) => {
  const listItemRefs = getSelectedNestedListItemRefs(editor)
  const lastListItemPath = listItemRefs[listItemRefs.length - 1]?.current

  // The items nested under the last outdented one are not a part of the selection,
  // so they have to be moved along with it to stay one level deeper than it
  const childSubListItemRef = getChildSubListItemRef(editor, lastListItemPath)
  const lastListItemRef = lastListItemPath ? Editor.pathRef(editor, lastListItemPath) : undefined

  Editor.withoutNormalizing(editor, () => {
    listItemRefs.forEach((listItemRef) => {
      // The first lift takes the item out of its own list, splitting that list when the item
      // is in the middle of it, so the neighbouring items keep their nesting level.
      // The second one takes the item out of the list item wrapping that list,
      // which places it next to it in the parent list
      if (listItemRef.current) {
        Transforms.liftNodes(editor, { at: listItemRef.current })
      }

      if (listItemRef.current) {
        Transforms.liftNodes(editor, { at: listItemRef.current })
      }

      listItemRef.unref()
    })
  })

  moveChildSubListUnderItem(editor, lastListItemRef, childSubListItemRef)
}
